import { useNavigate, useSearchParams } from 'react-router-dom';
import React from 'react';
import { Spin, Tag } from 'antd';
import BaseTable from '../../components/BaseTable';
import { useDeleteData, useFetchData, useCanvassStore } from '../../hooks';
import { useAuthStore, useDrawerStore } from '../../store';
import { PageableResponse } from '../../types';
import { CanvassRecord } from '../../types/Canvass';
import { Resource } from '../../lib/enums';
import { convertDate, dateFormat } from '../../lib/utils';

const statusColour: Record<string, string> = {
  DRAFT: 'default',
  CONFIRM: 'processing',
  APPROVED: 'success',
  CANCEL: 'error',
};

const CanvassTable = ({
  api,
  moduleName,
  moduleCode,
}: {
  api: string;
  moduleName: string;
  moduleCode: string;
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const drawerStore = useDrawerStore();
  const canvassStore = useCanvassStore();
  const { user } = useAuthStore();

  const userModuleAccess = user?.moduleAccess?.find(
    (item) => item.module.code === moduleCode,
  );

  const { data, isLoading } = useFetchData<PageableResponse<CanvassRecord>>(
    api,
    {
      resource: searchParams.get('resource') || Resource.MIN,
      search: searchParams.get('search') || '',
      length: parseInt(searchParams.get('length') || '999999'),
      active: searchParams.get('active') || true,
    },
  );

  const { mutate: deleteRecord } = useDeleteData(api);

  const columns = [
    {
      title: 'Canvass No.',
      dataIndex: 'code',
      key: 'code',
      // The code is the handle people use; make it the way into the detail.
      render: (code: string, record: CanvassRecord) => (
        <a onClick={() => navigate(`/purchase-canvass/${record.id}`)}>{code}</a>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'canvassDate',
      key: 'canvassDate',
      render: (value: string) => convertDate(value, dateFormat),
    },
    {
      title: 'For Request',
      dataIndex: 'purchaseRequest',
      key: 'purchaseRequest',
      render: (pr: { code?: string } | undefined) =>
        pr?.code ?? <span className={'text-gray-400'}>—</span>,
    },
    {
      title: 'Items',
      dataIndex: 'itemCount',
      key: 'itemCount',
      render: (n: number) => `${n ?? 0} items`,
    },
    {
      title: 'Suppliers',
      dataIndex: 'supplierCount',
      key: 'supplierCount',
      render: (n: number) => `${n ?? 0}`,
    },
    {
      title: 'Awarded To',
      dataIndex: 'selectedVendor',
      key: 'selectedVendor',
      // Empty until a manager has actually chosen, which is the point of the column.
      render: (v: { description?: string } | undefined) =>
        v?.description ?? <span className={'text-gray-400'}>not yet awarded</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColour[status] ?? 'default'}>{status}</Tag>
      ),
    },
  ];

  const handleEdit = (record: CanvassRecord) => {
    canvassStore.setData(record);
    // The store exposes one toggle rather than open/close; the other list
    // screens open the drawer the same way.
    drawerStore.onClose();
  };

  return (
    <Spin spinning={isLoading}>
      <BaseTable<CanvassRecord>
        dataSource={data?.data ?? []}
        isLoading={isLoading}
        module={moduleName}
        columns={columns}
        canEdit={userModuleAccess?.write}
        canDelete={userModuleAccess?.delete}
        handleEditButtonClick={handleEdit}
        handleDeleteButtonClick={(record) =>
          record.id ? deleteRecord({ id: record.id }) : undefined
        }
      />
    </Spin>
  );
};

export default CanvassTable;
